import { useQuery } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

// ── Types ──────────────────────────────────────────────────────────────

export interface MemoryRow {
  id?: number
  category: string
  key: string
  value: string
  source: string
  created_at: string
}

export interface SessionData {
  session_id: string
  active_intent: string
  sro_step: string
  sro_slots: Record<string, string>
  last_active_at: string
  turn_count: number
}

export interface InspectResponse {
  cp_uid: string
  session: SessionData | null
  memory: MemoryRow[]
}

export interface HistoryResponse {
  count: number
  next: string | null
  previous: string | null
  results: MemoryRow[]
}

export interface HistoryParams {
  category?: string
  key?: string
  source?: string
  ordering?: string
  page?: number
  page_size?: number
}

export interface GlobalHistoryParams extends HistoryParams {
  cp_uid?: string
  assistant_id?: string
}

export interface GlobalHistoryRow extends MemoryRow {
  cp_uid: string
  assistant_name: string | null
}

export interface GlobalHistoryResponse {
  count: number
  next: string | null
  previous: string | null
  results: GlobalHistoryRow[]
}

export interface TopKey {
  key: string
  category: string
  count: number
}

export interface GlobalStatsResponse {
  total_users: number
  total_facts: number
  top_keys: TopKey[]
}

// ── Hooks ──────────────────────────────────────────────────────────────

export function useMemoryInspect(assistantId: string, cpUid: string) {
  return useQuery<InspectResponse>({
    queryKey: ['memory-inspect', assistantId, cpUid],
    queryFn: async () => {
      const response = await knowledgeApi.get(
        `/knowledge/api/assistants/${assistantId}/memory/inspect/`,
        { params: { cp_uid: cpUid } },
      )
      return response.data
    },
    enabled: !!assistantId && !!cpUid,
    refetchInterval: 30_000,
  })
}

export function useMemoryHistory(
  assistantId: string,
  cpUid: string,
  params: HistoryParams = {},
) {
  const { category, key, source, ordering = '-created_at', page = 1, page_size = 25 } = params

  return useQuery<HistoryResponse>({
    queryKey: ['memory-history', assistantId, cpUid, category, key, source, ordering, page, page_size],
    queryFn: async () => {
      const response = await knowledgeApi.get(
        `/knowledge/api/assistants/${assistantId}/memory/history/`,
        {
          params: {
            cp_uid: cpUid,
            ...(category && { category }),
            ...(key && { key }),
            ...(source && { source }),
            ordering,
            page,
            page_size,
          },
        },
      )
      return response.data
    },
    enabled: !!assistantId && !!cpUid,
  })
}

// ── Global (tenant-wide) hooks ─────────────────────────────────────────

export function useGlobalMemoryStats() {
  return useQuery<GlobalStatsResponse>({
    queryKey: ['global-memory-stats'],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/memory/global/stats/')
      return response.data
    },
    staleTime: 0,
  })
}

export function useGlobalMemoryHistory(params: GlobalHistoryParams = {}) {
  const {
    cp_uid, assistant_id, category, key, source,
    ordering = '-created_at', page = 1, page_size = 25,
  } = params

  return useQuery<GlobalHistoryResponse>({
    queryKey: ['global-memory-history', cp_uid, assistant_id, category, key, source, ordering, page, page_size],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/memory/global/history/', {
        params: {
          ...(cp_uid && { cp_uid }),
          ...(assistant_id && { assistant_id }),
          ...(category && { category }),
          ...(key && { key }),
          ...(source && { source }),
          ordering,
          page,
          page_size,
        },
      })
      return response.data
    },
    staleTime: 0,
  })
}
